import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { STATUSES, PRIORITIES, WORK_TYPES, FORMATS, AUDIENCES } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { X, Loader2 } from "lucide-react";

const initial = {
  title: "", client_name: "", project: "", work_type: "Script",
  format: "Not Applicable", assigned_to: "", status: "Not Started", priority: "P1",
  due_date: "", google_doc_link: "", episode_no: "", audience: "Not Applicable",
  notes: "", related_doc_link: "",
  invoice_required: true,
};

export default function TaskFormModal({ open, onClose, onSaved, task = null }) {
  const { isAdmin, user } = useAuth();
  const [form, setForm] = useState(initial);
  const [ref, setRef] = useState({ clients: [], projects: [] });
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/reference").then((r) => setRef(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        ...initial, ...task,
        episode_no: task.episode_no ?? "",
        invoice_required: task.invoice_required ?? true,
      });
    } else {
      setForm({ ...initial, assigned_to: user?.id || "" });
    }
    setErr("");
  }, [open, task, user]);

  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const filteredProjects = ref.projects.filter(p => !form.client_name || p.client_name === form.client_name);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        episode_no: form.episode_no === "" ? null : Number(form.episode_no),
      };
      if (task) {
        await api.patch(`/tasks/${task.id}`, payload);
      } else {
        await api.post("/tasks", payload);
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed to save task.");
    } finally { setBusy(false); }
  };

  const canEditAdminFields = isAdmin || !task;

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
      <div
        className="bg-white max-w-2xl w-full rounded-xl shadow-xl border border-gray-200 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="task-form-modal"
      >
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight text-gray-900">
            {task ? "Edit task" : "Create task"}
          </h2>
          <button onClick={onClose} data-testid="task-modal-close" className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="overflow-y-auto px-6 py-5 space-y-4">
          <Field label="Task title" required>
            <input
              required value={form.title} onChange={(e) => setField("title", e.target.value)}
              data-testid="task-title-input"
              className="input"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Client" required>
              <select required value={form.client_name} onChange={(e) => { setField("client_name", e.target.value); setField("project", ""); }} data-testid="task-client-select" className="input" disabled={!canEditAdminFields}>
                <option value="">Select…</option>
                {ref.clients.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Project / IP" required>
              <select required value={form.project} onChange={(e) => setField("project", e.target.value)} data-testid="task-project-select" className="input" disabled={!canEditAdminFields}>
                <option value="">Select…</option>
                {filteredProjects.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Work type" required>
              <select required value={form.work_type} onChange={(e) => setField("work_type", e.target.value)} className="input" data-testid="task-work-type-select">
                {WORK_TYPES.map(w => <option key={w}>{w}</option>)}
              </select>
            </Field>
            <Field label="Format">
              <select value={form.format} onChange={(e) => setField("format", e.target.value)} className="input" data-testid="task-format-select">
                {FORMATS.map(f => <option key={f}>{f}</option>)}
              </select>
            </Field>
            <Field label="Audience">
              <select value={form.audience} onChange={(e) => setField("audience", e.target.value)} className="input" data-testid="task-audience-select">
                {AUDIENCES.map(a => <option key={a}>{a}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="Assigned to" required>
              <select required value={form.assigned_to} onChange={(e) => setField("assigned_to", e.target.value)} className="input" disabled={!canEditAdminFields} data-testid="task-assignee-select">
                <option value="">Select…</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Status" required>
              <select required value={form.status} onChange={(e) => setField("status", e.target.value)} className="input" data-testid="task-status-select">
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Priority" required>
              <select required value={form.priority} onChange={(e) => setField("priority", e.target.value)} className="input" disabled={!canEditAdminFields} data-testid="task-priority-select">
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Due date" required>
              <input required type="date" value={form.due_date?.slice(0, 10) || ""} onChange={(e) => setField("due_date", e.target.value)} className="input" disabled={!canEditAdminFields} data-testid="task-due-date-input" />
            </Field>
            <Field label="Episode / Script No.">
              <input type="number" value={form.episode_no} onChange={(e) => setField("episode_no", e.target.value)} className="input" data-testid="task-episode-input" />
            </Field>
          </div>

          <Field label="Google Doc link">
            <input type="url" placeholder="https://docs.google.com/..." value={form.google_doc_link || ""} onChange={(e) => setField("google_doc_link", e.target.value)} className="input" data-testid="task-gdoc-input" />
          </Field>

          <Field label="Related document link">
            <input type="url" placeholder="https://..." value={form.related_doc_link || ""} onChange={(e) => setField("related_doc_link", e.target.value)} className="input" data-testid="task-related-doc-input" />
          </Field>

          <Field label="Notes">
            <textarea rows={3} value={form.notes || ""} onChange={(e) => setField("notes", e.target.value)} className="input" data-testid="task-notes-input" />
          </Field>

          <label className="flex items-center gap-2.5 px-3 py-2.5 border border-gray-200 rounded-md bg-gray-50/50 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.invoice_required}
              onChange={(e) => setField("invoice_required", e.target.checked)}
              data-testid="task-invoice-required-checkbox"
              className="w-4 h-4 accent-[#2B4462]"
            />
            <span className="text-sm text-gray-700">
              <span className="font-medium text-gray-900">Invoice required</span>
              <span className="text-gray-500"> — uncheck for tasks that don't need to be billed (e.g. internal work).</span>
            </span>
          </label>

          {err && <div data-testid="task-form-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md">Cancel</button>
            <button type="submit" disabled={busy} data-testid="task-save-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded-md font-medium text-sm inline-flex items-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {task ? "Save changes" : "Create task"}
            </button>
          </div>
        </form>
      </div>
      <style>{`.input{width:100%;background:white;border:1px solid #d1d5db;border-radius:6px;padding:8px 10px;font-size:14px;color:#111827;outline:none;transition:all .15s}.input:focus{border-color:#2B4462;box-shadow:0 0 0 3px rgba(43,68,98,.12)}.input:disabled{background:#f9fafb;color:#6b7280}`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
